import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Temporal } from 'temporal-polyfill';
import type { ClassSeries, Location, Weekday } from '../domain/types';
import { formatSeriesSummary } from '../domain/format';
import { errorFor, validateSeries } from '../domain/validation';
import type { SeriesDraft, ValidationResult } from '../domain/validation';
import { useRepository } from './repositoryContext';
import { ErrorBanner, Field } from './components';
import { RecurrenceEditor } from './RecurrenceEditor';
import { CLASS_COLORS, colorName, nextClassColor } from '../domain/palette';
import { LocationField, browserTimeZone, type LocationChoice } from './LocationField';

/** Common class lengths, so duration is a tap rather than a typed number. */
const DURATIONS = [30, 45, 60, 75, 90];

function today(): string {
  return Temporal.Now.plainDateISO().toString();
}

function weekdayOf(isoDate: string): Weekday {
  return Temporal.PlainDate.from(isoDate).dayOfWeek as Weekday;
}

function emptyDraft(colorIndex: number): SeriesDraft {
  const start = today();
  return {
    title: '',
    locationId: '',
    color: nextClassColor(colorIndex),
    startTime: '18:00',
    durationMinutes: 60,
    // Default to repeating on whatever weekday today is — the most likely intent
    // when someone adds the class they teach.
    recurrence: { freq: 'weekly', byWeekday: [weekdayOf(start)] },
    startDate: start,
    endDate: null,
    notes: '',
    isSub: false,
  };
}

function toDraft(series: ClassSeries): SeriesDraft {
  return {
    title: series.title,
    locationId: series.locationId,
    color: series.color,
    startTime: series.startTime,
    durationMinutes: series.durationMinutes,
    recurrence: series.recurrence,
    startDate: series.startDate,
    endDate: series.endDate,
    notes: series.notes,
    isSub: series.isSub,
  };
}

export function ClassForm(props: {
  locations: readonly Location[];
  /** Omitted when creating. */
  series?: ClassSeries | undefined;
  /** How many classes already exist, so a new one starts on an unused colour. */
  seriesCount?: number;
  onSaved: () => void;
  onCancel: () => void;
  /** Editing only: archiving and deleting live here rather than cluttering the list. */
  onArchiveToggle?: (() => Promise<void>) | undefined;
  onDelete?: (() => Promise<void>) | undefined;
}): JSX.Element {
  const repo = useRepository();
  const editing = props.series !== undefined;

  const [draft, setDraft] = useState<SeriesDraft>(() =>
    props.series === undefined ? emptyDraft(props.seriesCount ?? 0) : toDraft(props.series),
  );
  const [place, setPlace] = useState<LocationChoice>(() => {
    if (props.series !== undefined) {
      return { kind: 'existing', id: props.series.locationId };
    }
    const first = props.locations[0];
    return first === undefined ? { kind: 'new', name: '' } : { kind: 'existing', id: first.id };
  });
  const [validation, setValidation] = useState<ValidationResult>({ ok: true });
  const [placeError, setPlaceError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [customDuration, setCustomDuration] = useState(
    () => !DURATIONS.includes(draft.durationMinutes),
  );

  function patch(changes: Partial<SeriesDraft>): void {
    setDraft((current) => ({ ...current, ...changes }));
  }

  const placeName =
    place.kind === 'existing'
      ? props.locations.find((location) => location.id === place.id)?.name
      : place.name;

  async function resolveLocationId(): Promise<string> {
    if (place.kind === 'existing') {
      return place.id;
    }
    // A typed place is created on save, which is what removes the old
    // "add a location first" dead end.
    const created = await repo.createLocation({
      name: place.name.trim(),
      address: '',
      timeZone: browserTimeZone(),
    });
    return created.id;
  }

  async function save(event: Event): Promise<void> {
    event.preventDefault();

    if (place.kind === 'new' && place.name.trim().length === 0) {
      setPlaceError('Where is this class?');
      return;
    }
    setPlaceError(undefined);

    // Validate everything except the location, which may not exist yet.
    const candidate: SeriesDraft = {
      ...draft,
      title: draft.title.trim(),
      notes: draft.notes.trim(),
      locationId: place.kind === 'existing' ? place.id : 'pending',
    };
    const result = validateSeries(candidate);
    setValidation(result);
    if (!result.ok) {
      return;
    }

    try {
      const locationId = await resolveLocationId();
      const payload: SeriesDraft = { ...candidate, locationId };
      if (props.series === undefined) {
        await repo.createSeries({ ...payload, active: true, archivedAt: null });
      } else {
        await repo.updateSeries(props.series.id, payload);
      }
      setError(undefined);
      props.onSaved();
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  }

  return (
    <form class="screen" onSubmit={(event) => void save(event)}>
      <header class="screen-bar">
        <button type="button" class="bar-action" onClick={props.onCancel}>
          Cancel
        </button>
        <h2>{editing ? 'Edit class' : 'New class'}</h2>
        <button type="submit" class="bar-action strong">
          Save
        </button>
      </header>

      <div class="screen-body">
        <ErrorBanner error={error} />

        <Field label="What" error={errorFor(validation, 'title')}>
          <input
            type="text"
            placeholder="Vinyasa Flow"
            value={draft.title}
            onInput={(event) => patch({ title: (event.target as HTMLInputElement).value })}
          />
        </Field>

        <Field label="Description (optional)">
          <textarea
            rows={2}
            value={draft.notes}
            onInput={(event) => patch({ notes: (event.target as HTMLTextAreaElement).value })}
          />
        </Field>

        <Field label="Colour" error={errorFor(validation, 'color')}>
          <div class="swatches" role="group" aria-label="Colour">
            {CLASS_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                class={`swatch${draft.color.toLowerCase() === color ? ' selected' : ''}`}
                style={{ background: color }}
                aria-label={colorName(color)}
                aria-pressed={draft.color.toLowerCase() === color}
                onClick={() => patch({ color })}
              />
            ))}
          </div>
        </Field>

        <LocationField
          locations={props.locations}
          value={place}
          error={placeError}
          onChange={setPlace}
        />

        {/* A label, not a behaviour change: sub classes schedule and export like
            any other, but the Classes list can tell them apart. */}
        <label class="list-option form-option">
          <span>I'm subbing this class</span>
          <input
            type="checkbox"
            role="switch"
            class="switch"
            checked={draft.isSub}
            onChange={(event) => patch({ isSub: (event.target as HTMLInputElement).checked })}
          />
        </label>

        <div class="inline-row">
          <Field label="Starts" error={errorFor(validation, 'startTime')}>
            <input
              type="time"
              value={draft.startTime}
              onInput={(event) => patch({ startTime: (event.target as HTMLInputElement).value })}
            />
          </Field>

          <Field label="Lasts" error={errorFor(validation, 'durationMinutes')}>
            {customDuration ? (
              <input
                type="number"
                min={5}
                max={480}
                step={5}
                value={draft.durationMinutes}
                onInput={(event) =>
                  patch({ durationMinutes: Number((event.target as HTMLInputElement).value) })
                }
              />
            ) : (
              <select
                value={String(draft.durationMinutes)}
                onChange={(event) => {
                  const raw = (event.target as HTMLSelectElement).value;
                  if (raw === 'custom') {
                    setCustomDuration(true);
                    return;
                  }
                  patch({ durationMinutes: Number(raw) });
                }}
              >
                {DURATIONS.map((minutes) => (
                  <option key={minutes} value={String(minutes)}>
                    {minutes === 60 ? '1 hour' : `${minutes} min`}
                  </option>
                ))}
                <option value="custom">Custom…</option>
              </select>
            )}
          </Field>
        </div>

        <RecurrenceEditor
          value={draft.recurrence}
          error={errorFor(validation, 'recurrence')}
          defaultWeekday={weekdayOf(draft.startDate)}
          onChange={(recurrence) =>
            // A one-time class has no "last class"; a lingering end date from a
            // previously chosen repeating rule could silently hide it.
            patch(recurrence.freq === 'once' ? { recurrence, endDate: null } : { recurrence })
          }
        />

        {/* Each rule shows only the dates it actually needs: a one-time class is
            a single date; a repeating one has a first class and an optional last. */}
        {draft.recurrence.freq === 'once' ? (
          <Field label="Date" error={errorFor(validation, 'startDate')}>
            <input
              type="date"
              value={draft.startDate}
              onInput={(event) => patch({ startDate: (event.target as HTMLInputElement).value })}
            />
          </Field>
        ) : (
          <div class="inline-row">
            <Field label="First class" error={errorFor(validation, 'startDate')}>
              <input
                type="date"
                value={draft.startDate}
                onInput={(event) => patch({ startDate: (event.target as HTMLInputElement).value })}
              />
            </Field>

            <Field
              label="Last class"
              error={errorFor(validation, 'endDate')}
              hint="Optional — leave blank to keep going."
            >
              <input
                type="date"
                value={draft.endDate ?? ''}
                onInput={(event) => {
                  const raw = (event.target as HTMLInputElement).value;
                  patch({ endDate: raw === '' ? null : raw });
                }}
              />
            </Field>
          </div>
        )}

        <p class="summary" aria-live="polite">
          {formatSeriesSummary({
            recurrence: draft.recurrence,
            startTime: draft.startTime,
            locationName: placeName,
          })}
        </p>

        {editing ? (
          <div class="danger-zone">
            {props.onArchiveToggle !== undefined ? (
              <button type="button" class="block" onClick={() => void props.onArchiveToggle?.()}>
                {props.series?.active === false ? 'Restore this class' : 'Archive this class'}
              </button>
            ) : null}
            {props.onDelete !== undefined ? (
              <button type="button" class="block danger" onClick={() => void props.onDelete?.()}>
                Delete this class
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </form>
  );
}
